/** Supported marker colour groups for reusable geographic point maps. */
export type GeoPointMarkerVariant = 'default' | 'site' | 'event' | 'job' | 'login';

/** Plain WGS84 latitude/longitude marker consumed by GeoPointMap. */
export interface GeoPointMarker {
    /** Stable unique id for React rendering and map clustering. */
    id: string;
    /** Human-readable popup label; do not pass raw ids here. */
    label: string;
    /** WGS84 decimal latitude. Null means the marker is excluded from the map. */
    latitude: number | null;
    /** WGS84 decimal longitude. Null means the marker is excluded from the map. */
    longitude: number | null;
    /** Visual colour group for the marker. */
    variant: GeoPointMarkerVariant;
    /** Optional short popup summary. */
    description?: string;
    /** Optional popup detail text or short detail list. */
    details?: string | readonly string[];
    /** Optional aggregate count shown on the pin. */
    pinCount?: number;
    /** True when the pin should visually pulse. */
    isLive?: boolean;
}

/** Marker after validation and 30 metre clustering. */
export interface GeoPointDisplayMarker {
    id: string;
    label: string;
    latitude: number;
    longitude: number;
    variant: GeoPointMarkerVariant;
    description?: string;
    details?: string | readonly string[];
    pinCount: number;
    isLive: boolean;
    markers: readonly GeoPointMarker[];
}

const nearbyClusterMetres = 30;
const earthRadiusMetres = 6371000;

const normalizePinCount = (value: number | undefined) => {
    if (value === undefined || !Number.isFinite(value) || value <= 0) {
        return 1;
    }

    return Math.trunc(value);
};

/** Returns true when a marker has finite WGS84 coordinates. */
export const isValidGeoPointMarker = (marker: GeoPointMarker): marker is GeoPointMarker & {
    latitude: number;
    longitude: number;
} =>
    marker.latitude !== null &&
    marker.longitude !== null &&
    Number.isFinite(marker.latitude) &&
    Number.isFinite(marker.longitude) &&
    marker.latitude >= -90 &&
    marker.latitude <= 90 &&
    marker.longitude >= -180 &&
    marker.longitude <= 180;

const toRadians = (value: number) => (value * Math.PI) / 180;

/** Calculates the haversine distance between two WGS84 points in metres. */
export const calculateGeoDistanceMetres = (
    first: Pick<GeoPointDisplayMarker, 'latitude' | 'longitude'>,
    second: Pick<GeoPointDisplayMarker, 'latitude' | 'longitude'>
) => {
    const latDelta = toRadians(second.latitude - first.latitude);
    const lonDelta = toRadians(second.longitude - first.longitude);
    const firstLat = toRadians(first.latitude);
    const secondLat = toRadians(second.latitude);
    const haversine = Math.sin(latDelta / 2) ** 2 +
        Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(lonDelta / 2) ** 2;

    return 2 * earthRadiusMetres * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const buildSingleDisplayMarker = (marker: GeoPointMarker & { latitude: number; longitude: number }): GeoPointDisplayMarker => ({
    id: marker.id,
    label: marker.label,
    latitude: marker.latitude,
    longitude: marker.longitude,
    variant: marker.variant,
    description: marker.description,
    details: marker.details,
    pinCount: normalizePinCount(marker.pinCount),
    isLive: marker.isLive ?? false,
    markers: [marker]
});

const buildClusterLabel = (markers: readonly GeoPointMarker[]) => {
    const firstLabel = markers[0]?.label ?? 'Mapped locations';
    return markers.length === 1 ? firstLabel : `${firstLabel} + ${markers.length - 1} nearby`;
};

const buildClusterDescription = (markers: readonly GeoPointMarker[], totalPins: number) => {
    if (markers.length === 1) {
        return markers[0]?.description;
    }

    return `${totalPins.toLocaleString()} total across ${markers.length.toLocaleString()} nearby locations`;
};

const mergeIntoCluster = (cluster: GeoPointDisplayMarker, marker: GeoPointMarker & { latitude: number; longitude: number }) => {
    const markerPins = normalizePinCount(marker.pinCount);
    const existingPins = cluster.pinCount;
    const nextPins = existingPins + markerPins;
    const nextMarkers = [...cluster.markers, marker];

    // Keep the cluster anchored to the weighted coordinate centre so dense nearby points stay stable.
    cluster.latitude = ((cluster.latitude * existingPins) + (marker.latitude * markerPins)) / nextPins;
    cluster.longitude = ((cluster.longitude * existingPins) + (marker.longitude * markerPins)) / nextPins;
    cluster.pinCount = nextPins;
    cluster.label = buildClusterLabel(nextMarkers);
    cluster.description = buildClusterDescription(nextMarkers, nextPins);
    cluster.details = nextMarkers.map((item) => item.label);
    cluster.isLive = cluster.isLive || Boolean(marker.isLive);
    cluster.markers = nextMarkers;
};

/** Filters invalid coordinates and manually clusters nearby markers within 30 metres. */
export const clusterGeoPointMarkers = (markers: readonly GeoPointMarker[]) => {
    const clusters: GeoPointDisplayMarker[] = [];

    markers.filter(isValidGeoPointMarker).forEach((marker) => {
        const existingCluster = clusters.find((cluster) =>
            calculateGeoDistanceMetres(cluster, marker) <= nearbyClusterMetres
        );

        if (existingCluster) {
            mergeIntoCluster(existingCluster, marker);
            return;
        }

        clusters.push(buildSingleDisplayMarker(marker));
    });

    return clusters;
};
